import { ScriptConverter, type ScriptOutputOption } from '../base.js';
import type {
  ContextualBlock,
  InteractiveScriptOptions,
  InteractiveScriptResult,
  ScriptLanguage,
} from '../../script-generator/types.js';

const BASH_LANGUAGES = new Set([
  'bash',
  'sh',
  'shell',
  'zsh',
  'ksh',
]);

export function normalizeBashLanguage(language: string | null): ScriptLanguage {
  if (!language) {
    return 'bash';
  }

  const lower = language.toLowerCase().trim();
  if (BASH_LANGUAGES.has(lower)) {
    return lower === 'sh' ? 'sh' : 'bash';
  }

  return 'other';
}

export function isBashLanguage(language: string | null): boolean {
  const normalized = normalizeBashLanguage(language);
  return normalized === 'bash' || normalized === 'sh';
}

/**
 * Bash-style block composition:
 * - Single block: unchanged
 * - Multiple blocks: append " && " to the last line of each block except last
 */
function composeWithAndChain(blocks: string[]): string {
  if (blocks.length === 0) return '';
  if (blocks.length === 1) return blocks[0];

  const result: string[] = [];

  for (let i = 0; i < blocks.length; i++) {
    const lines = blocks[i].split('\n');
    const isLastBlock = i === blocks.length - 1;

    if (lines.length === 0) continue;

    if (isLastBlock) {
      result.push(...lines);
      continue;
    }

    const lastLine = lines.pop()!;
    result.push(...lines);
    result.push(lastLine + ' && ');
  }

  return result.join('\n');
}

/**
 * The main interactive script template
 */
export const INTERACTIVE_TEMPLATE = `#!/usr/bin/env bash
#
# {{TITLE}}
# Generated by Leedoc Interactive Script Generator
# Timestamp: {{TIMESTAMP}}
# Total Steps: {{STEP_COUNT}}
#
# Usage:
#   ./script.sh              Run interactively
#   ./script.sh --auto       Run all steps without prompts
#   ./script.sh --resume     Resume from checkpoint
#   ./script.sh --restart    Start from beginning (clear checkpoint)
#
# Controls during execution:
#   Enter    Execute the step
#   s        Skip this step
#   q        Quit (progress saved)
#

set -euo pipefail

# Configuration
CHECKPOINT_FILE="\${LEEDOC_CHECKPOINT:-{{CHECKPOINT_FILE}}}"
ALLOW_RESUME={{ALLOW_RESUME}}
TOTAL_STEPS={{STEP_COUNT}}
AUTO_MODE=false

# Parse arguments
for arg in "$@"; do
  case "$arg" in
    --auto) AUTO_MODE=true ;;
    --resume) ALLOW_RESUME=true ;;
    --restart) rm -f "$CHECKPOINT_FILE" ;;
    --help|-h)
      head -n 20 "$0" | tail -n +2 | sed 's/^# //' | sed 's/^#//'
      exit 0
      ;;
  esac
done

# Terminal colors (only if stdout is a terminal)
if [ -t 1 ]; then
  RED='\\033[0;31m'
  GREEN='\\033[0;32m'
  YELLOW='\\033[1;33m'
  BLUE='\\033[0;34m'
  CYAN='\\033[0;36m'
  BOLD='\\033[1m'
  DIM='\\033[2m'
  NC='\\033[0m'
else
  RED='' GREEN='' YELLOW='' BLUE='' CYAN='' BOLD='' DIM='' NC=''
fi

# Current step tracking
CURRENT_STEP=0

#
# Checkpoint management
#

load_checkpoint() {
  if [ "$ALLOW_RESUME" = "true" ] && [ -f "$CHECKPOINT_FILE" ]; then
    local saved_step
    saved_step=$(cat "$CHECKPOINT_FILE" 2>/dev/null || echo 0)
    if [ "$saved_step" -gt 0 ] && [ "$saved_step" -lt "$TOTAL_STEPS" ]; then
      printf "\\n\${YELLOW}Found checkpoint at step %d/%d\${NC}\\n" "$saved_step" "$TOTAL_STEPS"
      if [ "$AUTO_MODE" = "false" ]; then
        printf "Resume from step %d? [Y/n/restart] " "$((saved_step + 1))"
        read -r response < /dev/tty || response=""
        case "$response" in
          n|N)
            CURRENT_STEP=0
            ;;
          restart|r|R)
            CURRENT_STEP=0
            rm -f "$CHECKPOINT_FILE"
            ;;
          *)
            CURRENT_STEP="$saved_step"
            ;;
        esac
      else
        CURRENT_STEP="$saved_step"
      fi
    fi
  fi
}

save_checkpoint() {
  local step_num="$1"
  CURRENT_STEP="$step_num"
  echo "$step_num" > "$CHECKPOINT_FILE"
}

clear_checkpoint() {
  rm -f "$CHECKPOINT_FILE"
}

#
# Display functions
#

print_header() {
  printf "\\n\${BOLD}\${CYAN}%s\${NC}\\n" "{{TITLE}}"
  printf "\${DIM}Total steps: %d\${NC}\\n\\n" "$TOTAL_STEPS"
}

print_step_header() {
  local step_num="$1"
  local heading="$2"
  printf "\\n\${BOLD}\${BLUE}════════════════════════════════════════════════════════════════\${NC}\\n"
  printf "\${BOLD}\${CYAN}Step %d/%d: %s\${NC}\\n" "$step_num" "$TOTAL_STEPS" "$heading"
  printf "\${BLUE}════════════════════════════════════════════════════════════════\${NC}\\n"
}

print_description() {
  local desc="$1"
  if [ -n "$desc" ]; then
    printf "\\n\${NC}%s\${NC}\\n" "$desc"
  fi
}

print_command_preview() {
  printf "\\n\${YELLOW}Commands to execute:\${NC}\\n"
  printf "\${DIM}────────────────────────────────────────\${NC}\\n"
}

print_preview_end() {
  printf "\${DIM}────────────────────────────────────────\${NC}\\n"
}

#
# User interaction
#

confirm_step() {
  if [ "$AUTO_MODE" = "true" ]; then
    return 0
  fi
  printf "\\n\${GREEN}Press Enter to execute, 's' to skip, 'q' to quit: \${NC}"
  read -r response < /dev/tty || response=""
  case "$response" in
    s|S) return 1 ;;
    q|Q)
      printf "\\n\${YELLOW}Aborted. Progress saved at step %d.\${NC}\\n" "$CURRENT_STEP"
      exit 0
      ;;
    *) return 0 ;;
  esac
}

prompt_on_failure() {
  local step_num="$1"
  if [ "$AUTO_MODE" = "true" ]; then
    printf "\\n\${RED}Step %d failed. Aborting (auto mode).\${NC}\\n" "$step_num"
    exit 1
  fi
  printf "\\n\${RED}Step %d failed.\${NC}\\n" "$step_num"
  printf "\${YELLOW}Continue anyway? [y/N] \${NC}"
  read -r response < /dev/tty || response=""
  case "$response" in
    y|Y) return 0 ;;
    *) exit 1 ;;
  esac
}

{{STEP_FUNCTIONS}}

#
# Main execution
#

main() {
  print_header
  load_checkpoint

{{STEP_CALLS}}

  clear_checkpoint
  printf "\\n\${GREEN}\${BOLD}✓ All steps completed successfully!\${NC}\\n\\n"
}

main "$@"
`;

/**
 * Template for a single step function
 */
export const STEP_FUNCTION_TEMPLATE = `
# Step {{STEP_NUM}}: {{HEADING}}
step_{{STEP_NUM}}_description() {
  cat <<'{{DESC_DELIM}}'
{{DESCRIPTION}}
{{DESC_DELIM}}
}

step_{{STEP_NUM}}_preview() {
  cat <<'{{PREVIEW_DELIM}}'
{{COMMAND_PREVIEW}}
{{PREVIEW_DELIM}}
}

step_{{STEP_NUM}}_execute() {
{{COMMAND_BODY}}
}
`;

/**
 * Template for step execution call
 */
export const STEP_CALL_TEMPLATE = `  # Step {{STEP_NUM}}
  if [ {{STEP_NUM}} -le "$CURRENT_STEP" ]; then
    printf "\${GREEN}✓ Step {{STEP_NUM}} already completed, skipping...\${NC}\\n"
  else
    print_step_header {{STEP_NUM}} "{{HEADING_ESCAPED}}"
    desc_{{STEP_NUM}}=$(step_{{STEP_NUM}}_description)
    print_description "$desc_{{STEP_NUM}}"
    print_command_preview
    step_{{STEP_NUM}}_preview
    print_preview_end
    if confirm_step; then
      printf "\\n\${CYAN}Executing...\${NC}\\n\\n"
      if step_{{STEP_NUM}}_execute; then
        printf "\\n\${GREEN}✓ Step {{STEP_NUM}} completed successfully\${NC}\\n"
        save_checkpoint {{STEP_NUM}}
      else
        prompt_on_failure {{STEP_NUM}}
        save_checkpoint {{STEP_NUM}}
      fi
    else
      printf "\${YELLOW}⊘ Step {{STEP_NUM}} skipped\${NC}\\n"
      save_checkpoint {{STEP_NUM}}
    fi
  fi
`;

export function escapeForShellSingleQuote(str: string): string {
  return str.replace(/'/g, "'\\''");
}

export function escapeForShellDoubleQuote(str: string): string {
  return str
    .replace(/\\/g, '\\\\')
    .replace(/\$/g, '\\$')
    .replace(/`/g, '\\`')
    .replace(/"/g, '\\"');
}

export function indentLines(str: string, spaces: number): string {
  const indent = ' '.repeat(spaces);
  return str
    .split('\n')
    .map(line => (line.trim() ? indent + line : line))
    .join('\n');
}

export function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).substring(0, 8);
}

export function generateUniqueDelimiter(content: string, prefix: string): string {
  const baseDelim = `__LEEDOC_${prefix}_`;
  let suffix = 0;
  let delim = `${baseDelim}${suffix}__`;

  while (content.includes(delim)) {
    suffix++;
    delim = `${baseDelim}${suffix}__`;
  }

  return delim;
}

function generateStepFunction(
  block: ContextualBlock,
  stepNum: number,
  _showPreview: boolean
): string {
  const heading = block.context.heading || `Step ${stepNum}`;
  const description = block.context.description || '';
  const commandContent = block.content;

  const descDelim = generateUniqueDelimiter(description, `DESC_${stepNum}`);
  const previewDelim = generateUniqueDelimiter(commandContent, `PREVIEW_${stepNum}`);

  return STEP_FUNCTION_TEMPLATE
    .replace(/\{\{STEP_NUM\}\}/g, String(stepNum))
    .replace(/\{\{HEADING\}\}/g, heading)
    .replace(/\{\{DESCRIPTION\}\}/g, description)
    .replace(/\{\{DESC_DELIM\}\}/g, descDelim)
    .replace(/\{\{COMMAND_PREVIEW\}\}/g, commandContent)
    .replace(/\{\{PREVIEW_DELIM\}\}/g, previewDelim)
    // Do not indent command body so heredoc terminators like EOF still work.
    .replace(/\{\{COMMAND_BODY\}\}/g, commandContent);
}

function generateStepCall(block: ContextualBlock, stepNum: number): string {
  const heading = block.context.heading || `Step ${stepNum}`;

  return STEP_CALL_TEMPLATE
    .replace(/\{\{STEP_NUM\}\}/g, String(stepNum))
    .replace(/\{\{HEADING_ESCAPED\}\}/g, escapeForShellDoubleQuote(heading));
}

export class BashScriptConverter extends ScriptConverter {
  readonly extensions = ['.sh'];

  composeBlocks(blocks: string[]): string {
    return composeWithAndChain(blocks);
  }

  getOutputOptions(): ScriptOutputOption[] {
    return [
      { value: 'standard', label: 'Standard' },
      { value: 'interactive', label: 'Interactive' },
    ];
  }

  detectScriptLanguage(blocks: ContextualBlock[]): ScriptLanguage {
    if (blocks.length === 0) {
      return 'bash';
    }

    const languages = blocks.map(block => normalizeBashLanguage(block.language));
    if (languages.some(language => language === 'other')) {
      return 'other';
    }

    return 'bash';
  }

  supportsInteractiveBlocks(blocks: ContextualBlock[]): boolean {
    return this.detectScriptLanguage(blocks) !== 'other';
  }

  generateInteractiveScript(
    blocks: ContextualBlock[],
    options: InteractiveScriptOptions = {}
  ): InteractiveScriptResult {
    if (blocks.length === 0) {
      return {
        success: false,
        error: 'No blocks provided',
      };
    }

    if (!this.supportsInteractiveBlocks(blocks)) {
      return {
        success: false,
        error: 'Script contains non-bash code blocks. Interactive mode only supports bash/sh.',
      };
    }

    const {
      title = 'Installation Script',
      checkpointFile = `/tmp/.leedoc-progress-${simpleHash(blocks[0].target)}`,
      showPreview = true,
      allowResume = true,
    } = options;

    try {
      const stepFunctions = blocks
        .map((block, index) => generateStepFunction(block, index + 1, showPreview))
        .join('\n');

      const stepCalls = blocks
        .map((block, index) => generateStepCall(block, index + 1))
        .join('\n\n');

      const script = INTERACTIVE_TEMPLATE
        .replace(/\{\{TITLE\}\}/g, escapeForShellDoubleQuote(title))
        .replace(/\{\{TIMESTAMP\}\}/g, new Date().toISOString())
        .replace(/\{\{STEP_COUNT\}\}/g, String(blocks.length))
        .replace(/\{\{CHECKPOINT_FILE\}\}/g, checkpointFile)
        .replace(/\{\{ALLOW_RESUME\}\}/g, allowResume ? 'true' : 'false')
        .replace(/\{\{STEP_FUNCTIONS\}\}/g, stepFunctions)
        .replace(/\{\{STEP_CALLS\}\}/g, stepCalls);

      return {
        success: true,
        script,
        stepCount: blocks.length,
      };
    } catch (err) {
      return {
        success: false,
        error: `Failed to generate interactive script: ${err}`,
      };
    }
  }
}
